import type { Workflow } from '@compass/client/types/workflow';
import { useDebounce } from '@hooks/useDebounce';
import {
  isValidFilter,
  useFilterParams,
  validateSearchValues,
  validateSortState,
} from '@hooks/useFilterParams';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type FiltersConfig, KNIPPER_PENDING_FILTER_ID } from '@/config/quickFilters.config';
import { type SupportedTimeZone } from '@/constants/date';
import { WorkflowType } from '@/constants/dispositions';
import type { SortState } from '@/types/table';
import { getPresetDateRange } from '@/utils/date';
import {
  type CachedFilterState,
  getCasesFilterCache,
  saveCasesFilterCache,
} from '@/utils/localStorage';

export type CaseTypeFilter = 'all' | 'AE' | 'PQC';

const formatNaiveDate = (d: { year: number; month: number; day: number }) =>
  `${d.year}-${String(d.month + 1).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;

/**
 * Default date range for a workflow as naive date strings (YYYY-MM-DD).
 * PQC workflow lands on today only; other workflows use the last 7 days.
 * Returned strings are timezone-independent — the timezone is applied at
 * API request time.
 */
export const getDefaultDateRange = (workflowType?: string, timeZone: SupportedTimeZone = 'ET') => {
  const presetName = workflowType === 'pqc' ? 'today' : 'last7';
  const range = getPresetDateRange(presetName, timeZone);
  return { from: formatNaiveDate(range.from), to: formatNaiveDate(range.to) };
};

/**
 * Range the PQC work-queue tabs (`hidesDatePicker`) are pinned to: today − 12
 * months → today. Independent of the picker, which still defaults to today.
 * An unbounded `created_at` query has no index-friendly range and timed out on
 * prod (AQ-863); a bounded one ran in ~0.3s.
 */
export const getPqcPinnedDateRange = (timeZone: SupportedTimeZone = 'ET') => {
  const { to } = getPresetDateRange('today', timeZone);
  // Date.UTC normalises the month underflow (Jan − 12 → the previous Jan).
  const d = new Date(Date.UTC(to.year, to.month - 12, to.day));
  return {
    from: formatNaiveDate({
      year: d.getUTCFullYear(),
      month: d.getUTCMonth(),
      day: d.getUTCDate(),
    }),
    to: formatNaiveDate(to),
  };
};

interface UseCasesFiltersArgs {
  selectedWorkflow: Workflow | undefined;
  selectedWorkflowId: string | undefined;
  selectedProgramId: string | undefined;
  workflows: Record<string, Workflow>;
  activeFiltersConfig: FiltersConfig;
  allowedFieldPaths: string[];
  timeZone: SupportedTimeZone;
}

/**
 * Owns all cases-page filter state + URL sync.
 *
 * Setters are write-through: each public `setX` updates local state AND
 * synchronously writes the new state to the URL. This replaces the previous
 * debounced URL-sync `useEffect` that raced with user navigation under
 * React-Router 7's `startTransition` location-state wrapping.
 *
 * Search input is the only filter that still writes URL from an effect, but
 * its deps are scoped to `[debouncedSearchValue, activeSearchType]` only —
 * deliberately excluding `location.pathname`, `currentPage`, `pageCursors`,
 * etc. — so it cannot race with a competing navigation.
 *
 * The workflow-switch effect below intentionally consolidates the
 * URL/cache/defaults reconciliation in one place. Splitting it further was
 * deferred to the larger Cases-page refactor; see the planning doc for
 * context.
 */
export function useCasesFilters(args: UseCasesFiltersArgs) {
  const {
    selectedWorkflow,
    selectedWorkflowId,
    selectedProgramId,
    workflows,
    activeFiltersConfig,
    allowedFieldPaths,
    timeZone,
  } = args;

  const { getInitialValues, syncFiltersToUrl, searchParams } = useFilterParams();
  const initialValues = getInitialValues();

  // Has the URL got any filter query params (excluding org/program prefixes)?
  const hasUrlFilters = useMemo(() => {
    const params = Array.from(searchParams.keys());
    const filterParams = params.filter(
      key => !['org', 'program'].includes(key) && searchParams.get(key),
    );
    return filterParams.length > 0;
  }, [searchParams]);

  // Cached filters fall through only when URL has no filters of its own.
  const cachedFilters = useMemo(() => {
    if (hasUrlFilters || !selectedWorkflowId || !selectedProgramId) {
      return null;
    }
    return getCasesFilterCache(selectedWorkflowId, selectedProgramId);
  }, [hasUrlFilters, selectedWorkflowId, selectedProgramId]);

  // Validate against allowed field paths to prevent URL parameter injection.
  const validatedSearchValues = useMemo(
    () => validateSearchValues(initialValues.searchValues || {}, allowedFieldPaths),
    [initialValues.searchValues, allowedFieldPaths],
  );
  const validatedSortState = useMemo(
    () => validateSortState(initialValues.sortState || [], allowedFieldPaths),
    [initialValues.sortState, allowedFieldPaths],
  );

  // ─── Local state ────────────────────────────────────────────────────────

  const [searchValues, setSearchValues] = useState<Record<string, string>>(() => {
    if (!hasUrlFilters && cachedFilters) {
      return cachedFilters.searchValues || {};
    }
    return validatedSearchValues;
  });

  const [activeSearchType, _setActiveSearchType] = useState<string>(() => {
    if (!hasUrlFilters && cachedFilters?.activeSearchType) {
      return cachedFilters.activeSearchType;
    }
    const activeField = Object.keys(validatedSearchValues).find(key => validatedSearchValues[key]);
    return activeField || 'case_id';
  });

  // PQC lands on the new Pending tab by default (AD-2206); other workflows
  // still default to All. Computed once based on the workflow type.
  const defaultQuickFilterId =
    selectedWorkflow?.workflow_type === WorkflowType.PQC ? KNIPPER_PENDING_FILTER_ID : 'all';

  const [activeQuickFilter, _setActiveQuickFilter] = useState<string>(() => {
    // When the URL has no filter param, fall through to cached → workflow default.
    // `getInitialValues()` returns `'all'` as a stand-in when `?filter=` is
    // absent — using that fallback as the initial filter overrides the PQC
    // default ('Pending', per AD-2206) on a fresh visit.
    if (!hasUrlFilters) {
      if (cachedFilters?.activeQuickFilter) {
        const cachedFilter = cachedFilters.activeQuickFilter;
        return isValidFilter(cachedFilter, activeFiltersConfig.filters)
          ? cachedFilter
          : defaultQuickFilterId;
      }
      return defaultQuickFilterId;
    }
    return isValidFilter(initialValues.filter, activeFiltersConfig.filters)
      ? initialValues.filter
      : defaultQuickFilterId;
  });

  const [assignedFilter, _setAssignedFilter] = useState<string[]>(() => {
    if (!hasUrlFilters && cachedFilters?.assignedFilter) {
      return cachedFilters.assignedFilter;
    }
    return initialValues.assigned || [];
  });

  const defaultRange = getDefaultDateRange(selectedWorkflow?.workflow_type, timeZone);

  const [dateFrom, _setDateFrom] = useState<string>(() => {
    if (!hasUrlFilters && cachedFilters?.dateFrom) {
      return cachedFilters.dateFrom;
    }
    return initialValues.dateFrom || defaultRange.from;
  });

  const [dateTo, _setDateTo] = useState<string>(() => {
    if (!hasUrlFilters && cachedFilters?.dateTo) {
      return cachedFilters.dateTo;
    }
    return initialValues.dateTo || defaultRange.to;
  });

  const [sortState, _setSortState] = useState<SortState[]>(() => {
    if (!hasUrlFilters && cachedFilters?.sortState) {
      return cachedFilters.sortState;
    }
    return validatedSortState;
  });

  const [caseTypeFilter, setCaseTypeFilter] = useState<CaseTypeFilter>('all');

  // Debounced search value for the active search type.
  const activeSearchValue = searchValues[activeSearchType] || '';
  const debouncedSearchValue = useDebounce(activeSearchValue, 500);

  // ─── URL write helper ───────────────────────────────────────────────────

  // Builds + writes URL from current state, with optional per-call overrides.
  // Replaces the deleted `syncFiltersToUrl` useEffect that raced with the
  // user's `navigate(...)` call when its `location.pathname` dep changed
  // mid-render.
  const writeUrl = useCallback(
    (
      overrides: Partial<{
        activeQuickFilter: string;
        searchValues: Record<string, string>;
        assignedFilter: string[];
        dateFrom: string;
        dateTo: string;
        sortState: SortState[];
      }> = {},
    ) => {
      const next = {
        activeQuickFilter: overrides.activeQuickFilter ?? activeQuickFilter,
        searchValues: overrides.searchValues ?? {
          ...searchValues,
          [activeSearchType]: debouncedSearchValue,
        },
        assignedFilter: overrides.assignedFilter ?? assignedFilter,
        dateFrom: overrides.dateFrom ?? dateFrom,
        dateTo: overrides.dateTo ?? dateTo,
        sortState: overrides.sortState ?? sortState,
      };
      syncFiltersToUrl(
        next.activeQuickFilter,
        next.searchValues,
        next.assignedFilter,
        next.dateFrom,
        next.dateTo,
        next.sortState,
        undefined, // pagination is owned by useCursorPagination
        true, // always replace; pagination handles its own URL updates
        selectedWorkflowId || undefined,
      );
    },
    [
      syncFiltersToUrl,
      activeQuickFilter,
      searchValues,
      activeSearchType,
      debouncedSearchValue,
      assignedFilter,
      dateFrom,
      dateTo,
      sortState,
      selectedWorkflowId,
    ],
  );

  // ─── Tab-defaults helper ────────────────────────────────────────────────

  // When the user navigates to a tab (initial landing or click), snap sort
  // to the tab's declared default. Pending uses created_at asc (most urgent
  // first); other PQC tabs use created_at desc. Tabs without `defaultSort`
  // (non-PQC configs) leave the user's sort alone.
  const applyTabDefaults = useCallback(
    (filterId: string): SortState[] | undefined => {
      const tab = activeFiltersConfig.filters.find(f => f.id === filterId);
      if (tab?.defaultSort) {
        _setSortState(tab.defaultSort);
        return tab.defaultSort;
      }
      return undefined;
    },
    [activeFiltersConfig.filters],
  );

  // ─── Write-through setters ──────────────────────────────────────────────

  const setActiveQuickFilter = useCallback(
    (filterId: string) => {
      _setActiveQuickFilter(filterId);
      const newDefaultSort = applyTabDefaults(filterId);
      writeUrl({
        activeQuickFilter: filterId,
        ...(newDefaultSort ? { sortState: newDefaultSort } : {}),
      });
    },
    [applyTabDefaults, writeUrl],
  );

  const setAssignedFilter = useCallback(
    (value: string[]) => {
      _setAssignedFilter(value);
      writeUrl({ assignedFilter: value });
    },
    [writeUrl],
  );

  const setDateRange = useCallback(
    (from: string, to: string) => {
      _setDateFrom(from);
      _setDateTo(to);
      writeUrl({ dateFrom: from, dateTo: to });
    },
    [writeUrl],
  );

  const setSortState = useCallback(
    (value: SortState[]) => {
      _setSortState(value);
      writeUrl({ sortState: value });
    },
    [writeUrl],
  );

  const setActiveSearchType = useCallback((type: string) => {
    _setActiveSearchType(type);
    // No URL write — search debounce effect below handles it on the next
    // debounced commit.
  }, []);

  // ─── Workflow-switch reset effect ───────────────────────────────────────

  // Track the workflow we've initialised filters for. We key on
  // `workflowId + workflowType` so the effect re-runs once workflows load
  // (workflow_type goes from undefined to e.g. 'pqc'): the initial run sees
  // `pqc-undefined` and resolves the wrong default, then the second run sees
  // `pqc-pqc` and applies the right one. Keying on workflowId alone would
  // skip the second run via the equality check and leave PQC stuck on 'All'.
  const prevWorkflowKeyRef = useRef<string | undefined>(undefined);
  // Re-armed each time the workflow-key effect proceeds, so the fallback
  // effect below can re-apply tab defaults for the new workflow context.
  const tabDefaultsAppliedRef = useRef(false);

  // Consolidates URL/cache/defaults reconciliation for 7 filter slots in one
  // place; splitting further (e.g. per-slot resolvers) is a deferred
  // follow-up. The complexity exception is scoped narrowly to this block.
  /* eslint-disable complexity */
  useEffect(() => {
    if (!selectedWorkflowId || !selectedProgramId) return;
    const workflowType = workflows[selectedWorkflowId]?.workflow_type;
    if (!workflowType) return;
    const workflowKey = `${selectedWorkflowId}:${workflowType}`;
    if (prevWorkflowKeyRef.current === workflowKey) return;

    prevWorkflowKeyRef.current = workflowKey;
    tabDefaultsAppliedRef.current = false;

    // Resolve the new filter state for this workflow once, then apply it AND
    // stamp the URL with the resolved values. The URL write here is safe from
    // the navigation race the rest of this hook avoids:
    //   - This effect's deps are workflow/program/timezone only — not
    //     `location.pathname`, not `currentPage`, not `pageCursors` — so it
    //     can't be re-triggered by a competing navigation under RR7's
    //     startTransition lag.
    //   - `prevWorkflowKeyRef` gates re-entry to a real workflow change.
    //
    // We need to stamp URL here because the next user action might be a
    // full-page reload (e.g. enabling developer mode in PharmBV), and if the
    // URL doesn't carry the new `workflow_id`, the store rehydrates to the
    // default workflow on reload — silently switching the user from PharmBV
    // to MedBV.
    const newCachedFilters = getCasesFilterCache(selectedWorkflowId, selectedProgramId);
    const newWorkflow = workflows[selectedWorkflowId];
    const newDefaultRange = getDefaultDateRange(newWorkflow?.workflow_type, timeZone);
    // AD-2206: PQC lands on the Pending tab; others stay on All.
    const newDefaultFilter =
      newWorkflow?.workflow_type === WorkflowType.PQC ? KNIPPER_PENDING_FILTER_ID : 'all';

    let resolved: {
      activeQuickFilter: string;
      searchValues: Record<string, string>;
      activeSearchType: string;
      assignedFilter: string[];
      dateFrom: string;
      dateTo: string;
      sortState: SortState[];
    };

    if (newCachedFilters) {
      resolved = {
        activeQuickFilter: isValidFilter(
          newCachedFilters.activeQuickFilter,
          activeFiltersConfig.filters,
        )
          ? newCachedFilters.activeQuickFilter
          : newDefaultFilter,
        searchValues: newCachedFilters.searchValues || {},
        activeSearchType: newCachedFilters.activeSearchType || 'case_id',
        assignedFilter: newCachedFilters.assignedFilter || [],
        dateFrom: newCachedFilters.dateFrom || newDefaultRange.from,
        dateTo: newCachedFilters.dateTo || newDefaultRange.to,
        sortState: newCachedFilters.sortState || [],
      };
    } else {
      const tab = activeFiltersConfig.filters.find(f => f.id === newDefaultFilter);
      resolved = {
        activeQuickFilter: newDefaultFilter,
        searchValues: {},
        activeSearchType: 'case_id',
        assignedFilter: [],
        dateFrom: newDefaultRange.from,
        dateTo: newDefaultRange.to,
        sortState: tab?.defaultSort ?? [],
      };
    }

    // Apply state. These setters batch into one render commit.
    setSearchValues(resolved.searchValues);
    _setActiveSearchType(resolved.activeSearchType);
    _setActiveQuickFilter(resolved.activeQuickFilter);
    _setAssignedFilter(resolved.assignedFilter);
    _setDateFrom(resolved.dateFrom);
    _setDateTo(resolved.dateTo);
    _setSortState(resolved.sortState);
    setCaseTypeFilter('all');

    // If we baked the tab default sort into `resolved.sortState` above, mark
    // it applied so the fallback effect below doesn't re-snap it.
    if (!newCachedFilters) {
      tabDefaultsAppliedRef.current = true;
    }

    // Stamp the URL with the resolved state + new workflow_id. Without this,
    // a refresh before the user interacts with any filter would lose the
    // workflow selection.
    syncFiltersToUrl(
      resolved.activeQuickFilter,
      resolved.searchValues,
      resolved.assignedFilter,
      resolved.dateFrom,
      resolved.dateTo,
      resolved.sortState,
      undefined,
      true,
      selectedWorkflowId,
    );
  }, [
    selectedWorkflowId,
    selectedProgramId,
    workflows,
    activeFiltersConfig.filters,
    timeZone,
    syncFiltersToUrl,
  ]);
  /* eslint-enable complexity */

  // AD-2206 follow-up: the workflow-key effect above can call applyTabDefaults
  // before `activeFiltersConfig` has switched from the default config to the
  // workflow-specific one (workflows load asynchronously). When that happens,
  // applyTabDefaults silently no-ops because the default config has no
  // matching tab id, so sortState stays empty on the landing tab. Re-apply
  // once we can see a real `defaultSort` for the active tab.
  useEffect(() => {
    if (tabDefaultsAppliedRef.current) return;
    if (hasUrlFilters) return;
    if (cachedFilters?.sortState?.length) return;
    const tab = activeFiltersConfig.filters.find(f => f.id === activeQuickFilter);
    if (!tab?.defaultSort) return;
    applyTabDefaults(activeQuickFilter);
    tabDefaultsAppliedRef.current = true;
  }, [
    activeFiltersConfig.filters,
    activeQuickFilter,
    applyTabDefaults,
    hasUrlFilters,
    cachedFilters,
  ]);

  // ─── Debounced search URL write ─────────────────────────────────────────

  // The ONLY remaining effect that writes URL from this hook. Deps are
  // intentionally scoped to the search debounce — not `location.pathname`,
  // not `currentPage`, not `pageCursors` — so it cannot be re-triggered by
  // a competing navigation. Safe from the racing-effect bug.
  useEffect(() => {
    writeUrl({
      searchValues: { ...searchValues, [activeSearchType]: debouncedSearchValue },
    });
    // We intentionally omit `searchValues` and `writeUrl` from the dep array.
    // Including `searchValues` would re-fire URL writes on every keystroke
    // before the 500ms debounce settles. Including `writeUrl` would re-fire
    // whenever any other filter changed, defeating the search-only scope and
    // re-introducing the location-coupled race surface this hook exists to
    // eliminate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue, activeSearchType]);

  // ─── Filter cache save ──────────────────────────────────────────────────

  useEffect(() => {
    if (!selectedWorkflowId || !selectedProgramId) {
      return;
    }
    const filterState: CachedFilterState = {
      activeQuickFilter,
      searchValues,
      activeSearchType,
      assignedFilter,
      dateFrom,
      dateTo,
      sortState,
    };
    saveCasesFilterCache(selectedWorkflowId, selectedProgramId, filterState);
  }, [
    selectedWorkflowId,
    selectedProgramId,
    activeQuickFilter,
    searchValues,
    activeSearchType,
    assignedFilter,
    dateFrom,
    dateTo,
    sortState,
  ]);

  return {
    // Read
    activeQuickFilter,
    searchValues,
    activeSearchType,
    debouncedSearchValue,
    assignedFilter,
    dateFrom,
    dateTo,
    sortState,
    caseTypeFilter,
    hasUrlFilters,
    cachedFilters,
    // Write-through setters
    setActiveQuickFilter,
    setSearchValues,
    setActiveSearchType,
    setAssignedFilter,
    setDateRange,
    setSortState,
    setCaseTypeFilter,
  };
}
